import AppFadeView from "@toothfairy/shared-ui/AppFadeView";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppMenu from "@toothfairy/shared-ui/AppMenu";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppUser from "@toothfairy/shared-ui/AppUser";
import React, { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import execEnvsHelper from "../../../../API/ExecutionEnvironments/execEnvsHelper";
import secretGenerator from "../../../../API/SecretGenerator";
import useWorkspaces from "../../../../Hooks/useWorkspaces";
import { isLargeScreen } from "../../../MainViewContainer";
import ExecEnvSettingsAIView from "../SubComponents/CustomExecutionSettingsAIView";
import semverHelper from "../../../../Utils/semverHelper";

const ExecEnvsSettings = forwardRef(
  (
    {
      sectionTitle,
      initialMapValues,
      settingType,
      isEdit,
      opt,
      setDisplayedView,
      backTo,
      onExecEnvCreated,
      selectedId,
      onExecEnvDeleted = () => {},
      availableAuthorisations,
      isAuthorisationsLoaded,
      isBuilderLayout,
      builderActiveSection,
      onBuilderContextChange,
      saveVersion,
    },
    ref
  ) => {
    const AppToasty = AppToast.useToast();
    const { t } = useTranslation();

    const { state, dispatch } = AppStateManager.useAppStateManager();
    const { getActiveWorkspace } = useWorkspaces();
    const { user } = AppUser.useUser();
    const [isSaveInProgress, setIsSaveInProgress] = React.useState(false);
    const validationSchema = AppForms.yup.object().shape({
      name: AppForms.yup
        .string()
        .required()
        .label("Name")
        .matches(
          /^[a-zA-Z0-9_]*$/,
          "Only alphanumeric characters and underscores are allowed without spaces."
        )
        .max(64, "Too long, maximum of 64 characters are allowed."),
      description: AppForms.yup
        .string()
        .required()
        .label("Description")
        .max(512),
      customCodeExecutionEnvironmentVersion: AppForms.yup
        .string()
        .label()
        .nullable()
        .matches(/^\d+\.\d+\.\d+$/, "Use format 1.2.3"),
    });

    const handleDeletion = async () => {
      try {
        const result =
          await execEnvsHelper.deleteCustomCodeExecutionEnvironment(opt.id);
        dispatch("SET_EXEC_ENVS", [
          ...state?.execEnvs?.filter((u) => u?.id !== opt.id),
        ]);
        onExecEnvDeleted();
        AppToasty.show(t("toasts.execHookDeleted"), {
          type: "success",
        });
      } catch (error) {
        console.log("Error deleting auth", error);
        AppToasty.show(t("toasts.execHookDeletionFailed"), {
          type: "danger",
        });
      }
    };
    const navigateBack = () => {
      setDisplayedView(backTo);
    };
    const identifyOptByID = (id) => {
      return state?.execEnvs.find((u) => u?.id === id);
    };
    const handleExecEnvItemSave = async (submitted) => {
      setIsSaveInProgress(true);
      let v = { ...submitted };
      v.workspaceID = getActiveWorkspace()?.id;
      v.customCodeExecutionEnvironmentVersion =
        semverHelper.normalizeVersion(
          v?.customCodeExecutionEnvironmentVersion
        ) || (isEdit ? null : "0.1.0");
      delete v?._lastChangedAt;
      delete v?.createdAt;
      delete v?.updatedAt;
      delete v?._deleted;
      delete v?.__typename;
      let newExecEnvs = [...state?.execEnvs];
      if (v?.customExecutionSecrets)
        v.customExecutionSecrets = JSON.stringify(
          v?.customExecutionSecrets?.map((u) => u?.id)
        );
      if (v?.staticDocs)
        v.staticDocs = JSON.stringify(
          v?.staticDocs?.map((u) => {
            return {
              id: u?.id,
              external_path: u?.external_path,
              title: u?.title,
            };
          })
        );
      if (v?.staticArtifacts)
        v.staticArtifacts = JSON.stringify(
          v?.staticArtifacts?.map((u) => {
            return {
              id: u?.id,
              external_path: u?.external_path,
              title: u?.title,
            };
          })
        );
      try {
        let result;
        if (isEdit) {
          result = await execEnvsHelper.updateCustomCodeExecutionEnvironment(
            opt.id,
            v,
            user?.id
          );
          const _updatedObject = identifyOptByID(
            result.data?.updateCustomCodeExecutionEnvironment?.id
          );
          const updateIndex = newExecEnvs.findIndex(
            (u) => u?.id === _updatedObject?.id
          );
          newExecEnvs[updateIndex] =
            result?.data?.updateCustomCodeExecutionEnvironment;
        } else {
          if (state?.execEnvs?.find((u) => u?.name === v?.name)) {
            AppToasty.show(t("toasts.execHookNameExists"), {
              type: "danger",
            });
            setIsSaveInProgress(false);
            return;
          }
          result = await execEnvsHelper.createCustomCodeExecutionEnvironment(
            {
              ...v,
            },
            user?.id
          );

          newExecEnvs.push(result?.data?.createCustomCodeExecutionEnvironment);
        }

        dispatch("SET_EXEC_ENVS", [...newExecEnvs]);
        backTo && navigateBack();
        const savedId =
          result?.data?.updateCustomCodeExecutionEnvironment?.id ||
          result?.data?.createCustomCodeExecutionEnvironment?.id;
        onExecEnvCreated && onExecEnvCreated("success", savedId, submitted);
      } catch (error) {
        console.error(error);
        onExecEnvCreated && onExecEnvCreated("error");
      } finally {
        setIsSaveInProgress(false);
      }
    };

    const _view = useMemo(
      () => (
        <AppFadeView
          style={
            isBuilderLayout
              ? {
                  flex: 1,
                  paddingHorizontal: 24,
                  paddingTop: 20,
                  paddingBottom: 40,
                  minWidth: 300,
                }
              : {
                  horizontalMargin: 10,
                  paddingHorizontal: 20,
                  minWidth: isLargeScreen() ? "100%" : 300,
                  paddingBottom: 10,
                  height: "auto",
                }
          }
        >
          {backTo && (
            <AppMenu.DefaultBackOptionComponent
              navigateBack={navigateBack}
              sectionTitle={"Execution hook : " + sectionTitle}
              isClickable
              isBackIconDisplayed
            />
          )}

          <AppForms.AppForm
            key={selectedId}
            ref={ref}
            initialValues={initialMapValues}
            onSubmit={handleExecEnvItemSave}
            validationSchema={validationSchema}
            enableReinitialize
          >
            <ExecEnvSettingsAIView
              handleDeletion={handleDeletion}
              isEdit={isEdit}
              settingType={settingType}
              isSaveInProgress={isSaveInProgress}
              availableAuthorisations={availableAuthorisations}
              isAuthorisationsLoaded={isAuthorisationsLoaded}
              isBuilderLayout={isBuilderLayout}
              builderActiveSection={builderActiveSection}
              onBuilderContextChange={onBuilderContextChange}
              saveVersion={saveVersion}
            />
          </AppForms.AppForm>
        </AppFadeView>
      ),
      [
        state.activeWorkspaceID,
        initialMapValues,
        state?.execEnvs,
        isEdit,
        AppToasty,
        isSaveInProgress,
        isBuilderLayout,
        builderActiveSection,
        saveVersion,
        availableAuthorisations,
        isAuthorisationsLoaded,
      ]
    );
    return _view;
  }
);

export default ExecEnvsSettings;
